import {
  Component,
  ViewChild,
  ViewChildren,
  QueryList,
  Input,
  Output,
  EventEmitter,
  TemplateRef,
  signal,
  effect,
  HostBinding,
  ElementRef,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  input,
  computed,
  EffectRef,
  OnDestroy,
} from '@angular/core'
import { DropdownLazyLoadEvent } from 'primeng/dropdown'
import {
  ColumnFilter,
  Table,
  TableColResizeEvent,
  TableRowCollapseEvent,
  TableRowExpandEvent,
} from 'primeng/table'
import {
  COUNTRY_EU_CODES,
  COVERAGE_TYPES,
  CatalogLocalePipe,
  CatalogLocales,
  Channel,
  Coverage,
  Currency,
  EntityStatusDesc,
  FsPipe,
  Location,
  Manufacturer,
  OrderRow,
  OrderRowProductExtended,
  PRODUCT_TYPES,
  PickingListOrder,
  PickingUnusableProduct,
  Product,
  ProductBarcode,
  ProductScope,
  ProductSite,
  QuerytStringSortOrder,
  Role,
  StatusHistory,
  User,
  UserRole,
  getCatalogValue,
  getFileDate,
  parseUser,
  Supplier,
  ProductSupplier,
  ProductCatalogCategory,
  Category,
  AttributeOption,
  GoodsReceive,
  EXECUTION_STATUSES,
  ExecutionStatus,
  Activity,
  Execution,
  OrderShipment,
} from '@evologi/shared/data-access-api'
import { Cell, Workbook } from 'exceljs'
import { saveAs } from 'file-saver'
import { get } from 'lodash'
import { CalendarComponent } from '../calendar/calendar.component'
import { ClipboardService } from 'ngx-clipboard'
import { SortEvent } from 'primeng/api'
import { isLightColor } from '@evologi/shared/util-toolkit'
import { AdminUIsRepository } from '../../admin-ui.repository'
import { OpMenuItem } from '../menu-ng/menu-ng.component'
import { TranslationService } from '@evologi/shared/util-translation'
import { ButtonColor, ButtonSize } from '../button/button-ng.component'

export interface TableColumn {
  label: string
  field: string
  dataKey?: string
  template?: TableColumnTemplate
  type?: TableColumnType
  dateFormat?: string
  width: number
  maxWidth?: number
  cssClass?: string
  enableSorting?: boolean
  sortOrder?: QuerytStringSortOrder
  enableFiltering?: boolean
  enableHeaderFiltering?: boolean
  enableEditing?: boolean
  enableOperatorFiltering?: boolean
  operator?: TableOperator
  dataKeyField?: TableColumnDataKeyField
  dataDisplayField?: TableColumnDataDisplayField
  dataSourceKey?: TableColumnDataSourceKey | string
  groupKey?: string // Key to associate column with a group
  excludeFromExport?: boolean
  tooltipLabel?: string
  align?: 'left' | 'center' | 'right'
  disableDecimals?: boolean
  multiSelect?: boolean
}

export interface TableColumnGroup {
  key: string // Unique identifier for the group
  label: string // Display label for the group header
}

export type ExportableValue =
  | string
  | number
  | boolean
  | Date
  | Array<object>
  | null
  | undefined
  | CatalogLocales<string>[]

export interface ExportCellContext<T> {
  data: T
  column: TableColumn
  value: ExportableValue
  excelCell?: Cell
  dataLookups?: Record<string, object[] | undefined>
}

export type TableColumnTemplate =
  | 'abcClassBadge'
  | 'aisle'
  | 'area'
  | 'attributeMultiSelect'
  | 'attachments'
  | 'availableQty'
  | 'barcodes'
  | 'bulkUpdateError'
  | 'bulkUpdateResults'
  | 'carrier'
  | 'catalogLocales'
  | 'clipboard'
  | 'compartment'
  | 'completion'
  | 'coverageOrder'
  | 'coverageOrderPicking'
  | 'currency'
  | 'defaultValue'
  | 'eecCountry'
  | 'executionStatus'
  | 'familyCompleteness'
  | 'goodsReceive'
  | 'goodsReceiveByOrdNum'
  | 'goodsReceiveStatus'
  | 'image'
  | 'importantNote'
  | 'incomingQty'
  | 'manufacturerName'
  | 'movedQty'
  | 'notes'
  | 'onHandQty'
  | 'orderPackages'
  | 'packedOrders'
  | 'packagesVolume'
  | 'packagesWeight'
  | 'packingActions'
  | 'path'
  | 'pickingListRef'
  | 'pickProduct'
  | 'position'
  | 'price'
  | 'priceWithCatalog'
  | 'priceWithFour'
  | 'priority'
  | 'productCategories'
  | 'productLink'
  | 'productName'
  | 'productParent'
  | 'productPhoto'
  | 'productSites'
  | 'productQties'
  | 'qty'
  | 'receivingQty'
  | 'returnedRowsCount'
  | 'role'
  | 'rowsOrderedCount'
  | 'rowsReturnedCount'
  | 'rowsScrappedCount'
  | 'rowsCount'
  | 'sales'
  | 'salesChannel'
  | 'selectRow'
  | 'scrappedQty'
  | 'shelf'
  | 'sku'
  | 'status'
  | 'statusHistoryTime'
  | 'stHiPackedBy'
  | 'stHiProcessedBy'
  | 'suppliers'
  | 'suppliersSku'
  | 'tagModel'
  | 'tags'
  | 'tooltip'
  | 'trackingNumber'
  | 'unusableOrder'
  | 'user'

export interface TableActionsContext<T> {
  item: T
}

type TableColumnType = 'string' | 'number' | 'date' | 'boolean'

type TableOperator = 'contains' | '=' | '<>'

type TableColumnDataKeyField =
  | '_id'
  | 'alpha2Code'
  | 'label.default'
  | 'value'
  | 'code'

type TableColumnDataDisplayField =
  | 'businessName'
  | 'code'
  | 'customName'
  | 'header.orderNumber'
  | 'label'
  | 'label.default'
  | 'name'
  | 'name.default'
  | 'name|surname'
  | 'SKU'
  | 'status'
  | 'value'

type TableColumnDataSourceKey =
  | 'brands'
  | 'bulkUpdateResults'
  | 'carriers'
  | 'categories'
  | 'cee'
  | 'channels'
  | 'countries'
  | 'coverage'
  | 'currencies'
  | 'families'
  | 'goodsReceives'
  | 'invoiceTypes'
  | 'kits'
  | 'locations'
  | 'manufacturers'
  | 'orders'
  | 'parents'
  | 'payments'
  | 'products'
  | 'priorities'
  | 'productTypes'
  | 'promotionTypes'
  | 'reasons'
  | 'roles'
  | 'status'
  | 'suppliers'
  | 'tags'
  | 'targets'
  | 'transports'
  | 'types'
  | 'users'
  | 'warehouses'

type TableOffset = '0px' | '20px' | '150px' | '300px'

export type ProuctQtyType =
  | 'onHandQty'
  | 'availableQty'
  | 'orderedQty'
  | 'incomingQty'
  | 'scrappedQty'
  | 'sales'

export type NumberOperator = 'equals' | 'gt' | 'gte' | 'lt' | 'lte'

export interface TableFilters {
  [key: string]: string | string[] | Date | Date[] | undefined
}

export interface LookupSearch {
  dataSourceKey: string
  q?: string
  dataKeyField?: string
  dataDisplayField?: string
  offset?: number
  limit?: number
}

export type RowLinkAction = 'edit' | 'quickView' | 'view'

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'op-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  providers: [FsPipe],
})
export class TableComponent<T> implements AfterViewInit, OnDestroy {
  @ViewChild('table', { static: true }) table?: Table
  @ViewChildren('calendar') calendars?: QueryList<CalendarComponent>
  @ViewChildren('textFilter') textFilters?: QueryList<ColumnFilter>

  // Entity
  @Input({ required: true }) getData!: (
    exportAll?: boolean,
    offset?: number,
    limit?: number,
    sort?: SortEvent,
  ) => Promise<T[]>
  @Input() totalCount?: number
  @Input() entityName = ''
  @Input() rowLink?: (item: T, action: RowLinkAction) => string | undefined
  @Input() currencyCode: string | undefined = 'EUR'
  @Input() scope?: ProductScope
  @Input() dataLookups?: Record<string, object[] | undefined>
  @Input() dropdownOptions?: Record<string, object[] | undefined>

  // Columns
  columns = input.required<TableColumn[]>()
  @Input() columnGroups?: TableColumnGroup[]
  @Input() actionsTemplate?: TemplateRef<TableActionsContext<T>>
  @Output() lookupSearch = new EventEmitter<LookupSearch>()

  // Row Expansion
  @Input() expansionTemplate?: TemplateRef<{ item: T }>

  // Status
  @Input() editable = true
  @Input() isLoading = false
  @Input() isError = false
  @Input() isInitialized = false
  @Input() isFiltered = false

  // Table
  @Input() showEditButton = true
  @Input() showViewButton = false
  @Input() showDeleteButton = true
  @Input() showResetButton = true
  @Input() showCreateButton = true
  @Input() showQuickViewButton = false
  @Input() showSkeleton = true
  @Input() disableSelection = false
  @Input() selectionLimit?: number
  @Input() dataKey = '_id'
  @Input() tableOffset: TableOffset = '0px'
  @Input() stickyPaginator = true
  @Input() hidePaginator = false
  @Input() bottomPadding: '120px' | '0px' = '120px'
  @Input() altDeleteIcon?: string
  @Input() altDeleteTooltip?: string

  @Input() quickViewButtonLabel?: string
  @Input() quickViewButtonSize?: ButtonSize
  @Input() quickViewButtonIcon?: string
  @Input() quickViewButtonTooltip?: string
  @Input() quickViewButtonColor?: ButtonColor

  @Input() extraButtonLabel?: string
  @Input() extraButtonSize?: ButtonSize
  @Input() extraButtonIcon?: string
  @Input() extraButtonTooltip?: string
  @Input() extraButtonColor?: ButtonColor
  @Input() showExtraButton = false

  @Input() rowMenuItems?: (item: T) => OpMenuItem[]
  @Input() altDeleteFunc?: (item: T) => void
  @Input() altDeleteCondition?: (item: T) => boolean
  shouldShowAltDelete(item: T): boolean {
    return !!(!this.altDeleteCondition || this.altDeleteCondition(item))
  }
  @Input() selectOnQtyEdit = false

  @HostBinding('style.--table-offset')
  get tableOffsetStyle(): string {
    return this.tableOffset
  }
  @Input() rowClass?: (item: T) => string
  @Input() selectRowByIndex?: (index: number) => void
  @Input() pickProductQty?: (row: OrderRow) => void
  @Input() pickSimpleProductQty?: (
    simpleProduct: OrderRowProductExtended,
  ) => void

  // Excel export
  @Input() customizeExportCell?: (
    context: ExportCellContext<T>,
  ) => ExportableValue
  @Input() fileName = ''
  @Input() actionsColumnWidth: 25 | 50 | undefined

  // Emitters
  @Output() readonly itemEdit = new EventEmitter<T>()
  @Output() readonly itemQuickView = new EventEmitter<T>()
  @Output() readonly itemCreate = new EventEmitter<void>()
  @Output() readonly itemDelete = new EventEmitter<T>()
  @Output() readonly itemsSelect = new EventEmitter<T[]>()
  @Output() readonly itemUpdate = new EventEmitter<T>()
  @Output() readonly itemExtraAction = new EventEmitter<T>()
  @Output() readonly filterSelect = new EventEmitter<TableFilters>()
  @Output() readonly rowExpand = new EventEmitter<TableRowExpandEvent>()
  @Output() readonly rowCollapse = new EventEmitter<TableRowCollapseEvent>()
  @Output() readonly gridReset = new EventEmitter<void>()
  @Output() readonly qtyChange = new EventEmitter<{
    _id: string
    qty: number
  }>()
  @Output() readonly pageChange = new EventEmitter<{
    first: number
    rows: number
  }>()
  @Output() readonly productShowQties = new EventEmitter<{
    product: Product
    qtyType: ProuctQtyType
  }>()

  public data: T[] = []
  public selectedItems: T[] = []
  public expandedRows: { [key: string]: boolean } = {}
  public rowsPerPageOptionsArray = [
    { label: '15', value: 15 },
    { label: '30', value: 30 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
    { label: '200', value: 200 },
  ]
  public filterDelay = 2500
  public dropdownFilterValues: Record<string, string | string[]> = {}
  public primeOperator = 'between'
  public currentPage = 1
  public rows = 15
  public firstRecord = 0
  public loadedOnce = false
  public readonly skeletonRows = Array.from({ length: 15 }, (_, i) => i)
  private requestId = 0

  public ueCountries = COUNTRY_EU_CODES

  // Signal to track table filters
  public tableFilters = signal<TableFilters>({})
  private isFirstFilterEffect = true
  private columnWidthOverrides = signal<Record<number, number>>({})
  private justResizedColumn = false
  private readonly dropdownLookupPageSize = 40
  private headerDropdownFilterText: Record<string, string> = {}

  hasColumnGroups = computed(() =>
    Boolean(this.columns().some((c) => c.groupKey)),
  )
  public get isEditingCell(): boolean {
    const cell = this.table?.editingCell
    if (!cell) return false

    return cell.hasAttribute('ng-reflect-data')
  }
  public numberFilterValues: Record<string, string> = {}
  public numberFilterOperators: Record<string, string> = {}
  public numberOperatorOptions = [
    { label: '=', value: '=' },
    { label: '>', value: '>' },
    { label: '>=', value: '>=' },
    { label: '<', value: '<' },
    { label: '<=', value: '<=' },
  ]
  public templateMap: Record<string, TemplateRef<unknown>> = {}

  columnMeta = computed(() =>
    this.columns().map((col, i) => ({
      displayWidth: this.columnWidthOverrides()[i] ?? col.width,
      isFirst: col.groupKey
        ? this.columns().filter((c) => c.groupKey === col.groupKey)[0] === col
        : false,
      isLast: col.groupKey
        ? [
            ...this.columns().filter((c) => c.groupKey === col.groupKey),
          ].pop() === col
        : false,
      groupWidth: col.groupKey ? this.getGroupDisplayWidth(col.groupKey) : 0,
    })),
  )

  get pencilAction(): RowLinkAction {
    return this.showQuickViewButton ? 'quickView' : 'edit'
  }

  rowFragment(item: T, action: RowLinkAction): string | undefined {
    return this.rowLink?.(item, action) || undefined
  }

  get actionsColWidth(): number {
    if (this.actionsColumnWidth != null) return this.actionsColumnWidth
    if (this.quickViewButtonSize === 'big') return 125
    if (!this.showResetButton) return 50
    return (this.rowMenuItems?.length ?? 0) > 0 ? 100 : 80
  }

  numberOperatorShell(column: TableColumn): boolean {
    return (
      !column.enableHeaderFiltering &&
      column.enableFiltering === true &&
      column.enableOperatorFiltering === true &&
      !!column.field &&
      column.type === 'number'
    )
  }

  getColumnsByGroup(groupKey: string): TableColumn[] {
    return this.columns().filter((col) => col.groupKey === groupKey)
  }

  getColumnDisplayWidth(column: TableColumn, colIndex: number): number {
    const w = this.columnWidthOverrides()[colIndex] ?? column.width
    return column.maxWidth != null ? Math.min(w, column.maxWidth) : w
  }

  private filterTimeoutRef?: ReturnType<typeof setTimeout>
  private effectRef?: EffectRef

  constructor(
    private elementRef: ElementRef,
    private fsPipe: FsPipe,
    private catalogLocalePipe: CatalogLocalePipe,
    private translationService: TranslationService,
    private cdr: ChangeDetectorRef,
    private clipboardService?: ClipboardService,
    private adminUIsRepository?: AdminUIsRepository,
  ) {
    // Watch for filter changes and emit them after delay (debounced)
    this.effectRef = effect(() => {
      const filters = this.tableFilters()
      if (this.filterTimeoutRef) clearTimeout(this.filterTimeoutRef)
      this.filterTimeoutRef = setTimeout(() => {
        // Filter out all keys that have undefined values
        const filteredFilters = Object.fromEntries(
          Object.entries(filters).filter(([, value]) => value !== undefined),
        ) as TableFilters

        this.filterDelay = 2500
        if (
          this.isFirstFilterEffect &&
          Object.keys(filteredFilters).length === 0
        ) {
          this.isFirstFilterEffect = false
        } else {
          this.currentPage = 1
          this.firstRecord = 0
          this.isFirstFilterEffect = false
          this.filterSelect.emit(filteredFilters)
        }
      }, this.filterDelay)
    })
  }

  ngAfterViewInit(): void {
    this.checkIfScrollable()
    this.setTemplateMap()

    this.cdr.markForCheck()
    setTimeout(() => this.checkIfScrollable(), 100)
  }

  ngOnDestroy(): void {
    if (this.filterTimeoutRef) clearTimeout(this.filterTimeoutRef)
    this.effectRef?.destroy()
  }

  // For column arrays
  trackByField(_: number, col: TableColumn): string {
    return col.field
  }

  // For data rows (used by *ngFor if you ever add one,
  // but PrimeNG uses [dataKey] instead — already set)
  trackByDataKey(index: number, item: T): unknown {
    return (item as Record<string, unknown>)[this.dataKey] ?? index
  }

  // For page numbers in paginator
  trackByPage(_: number, page: number): number {
    return page
  }

  // For tag strings
  trackByTag(_: number, tag: string): string {
    return tag
  }

  trackByRowId = (index: number, item: T): string | number => {
    const record = item as Record<string, unknown>
    const key = record[this.dataKey]

    if (typeof key === 'string' || typeof key === 'number') {
      return key
    }

    // No stable key available — fall back to index
    // This means no reuse optimization, but no incorrect reuse either
    return index
  }

  private checkIfScrollable(): void {
    const wrapper = this.elementRef.nativeElement.querySelector(
      '.p-datatable-wrapper',
    )
    if (wrapper) {
      const isScrollable = wrapper.scrollHeight > wrapper.clientHeight
      if (isScrollable) {
        wrapper.classList.add('has-scroll')
      } else {
        wrapper.classList.remove('has-scroll')
      }
    }
  }

  // Table properties methods

  onSelectionChange(items: T[]) {
    if (!this.selectionLimit || items.length <= this.selectionLimit) {
      this.selectedItems = items
      this.itemsSelect.emit(this.selectedItems)
    } else {
      const prev = [...this.selectedItems]
      this.selectedItems = []
      setTimeout(() => {
        this.selectedItems = prev
      })
      this.adminUIsRepository?.notify({ code: 'SELECTION_LIMIT_REACHED' })
    }
  }

  onColumnResize(event: TableColResizeEvent): void {
    this.justResizedColumn = true
    const draggedCell = event.element as HTMLElement
    const colIndexAttr = draggedCell.getAttribute('data-column-index')
    if (!colIndexAttr) return

    const index = Number(colIndexAttr)
    const column = this.columns()[index]
    if (!column) return

    let newWidth = draggedCell.offsetWidth

    if (column.maxWidth != null && newWidth > column.maxWidth) {
      newWidth = column.maxWidth
      draggedCell.style.width = `${newWidth}px`
    }

    this.columnWidthOverrides.update((prev) => ({ ...prev, [index]: newWidth }))

    // In "fit" mode, PrimeNG also resizes the neighbor column.
    // Find it and record its width too, so Angular doesn't reset it.
    const nextTh = draggedCell.nextElementSibling as HTMLElement | null
    if (nextTh) {
      const nextIndexAttr = nextTh.getAttribute('data-column-index')
      if (nextIndexAttr) {
        const nextIndex = Number(nextIndexAttr)
        const nextColumn = this.columns()[nextIndex]
        let nextWidth = nextTh.offsetWidth

        if (nextColumn?.maxWidth != null && nextWidth > nextColumn.maxWidth) {
          nextWidth = nextColumn.maxWidth
          nextTh.style.width = `${nextWidth}px`
        }

        this.columnWidthOverrides()[nextIndex] = nextWidth
      }
    }
    setTimeout(() => (this.justResizedColumn = false), 100)
  }

  updateTotalCount() {
    if (this.table && this.totalCount) {
      this.table.totalRecords = this.totalCount
      this.table._totalRecords = this.totalCount
    }
  }

  hasFilterableColumns = computed(() =>
    this.columns().some((c) => c.enableFiltering || c.enableHeaderFiltering),
  )

  showActionsColumn = computed(
    () =>
      this.showEditButton ||
      this.showViewButton ||
      this.showDeleteButton ||
      this.showResetButton ||
      this.showCreateButton,
  )

  openProductInNewTab(event: MouseEvent, productId: string, sku?: string) {
    const target = event.target as HTMLElement
    if (target.closest('.btn-clipboard') || target.closest('button')) {
      if (this.clipboardService && sku) {
        this.clipboardService.copyFromContent(sku)
      }
      event.preventDefault()
      event.stopPropagation()
      return
    }

    event.preventDefault()
    event.stopPropagation()
    window.open(`/products#product/${productId}`, '_blank')
  }

  // Table methods

  private getCurrentSortEvent(): SortEvent | undefined {
    const t = this.table
    if (!t?.sortField) {
      return undefined
    }
    return {
      field: t.sortField,
      order: (t.sortOrder ?? 1) as 1 | -1,
    }
  }

  private async load(
    first: number,
    rows: number,
    sort?: SortEvent,
  ): Promise<void> {
    const id = ++this.requestId
    try {
      const result = await this.getData(
        false,
        first,
        rows,
        sort ?? this.getCurrentSortEvent(),
      )
      if (id !== this.requestId) {
        // superseded by a newer load
        return
      }
      this.data = result
      this.updateTotalCount()
    } catch (error) {
      if (id !== this.requestId) {
        // Stores expose the error through isError; just don't leave it unhandled
        return
      }
    } finally {
      if (id === this.requestId) {
        this.loadedOnce = true
        this.cdr.markForCheck()
      }
    }
  }

  get skeletonVisible(): boolean {
    return this.showSkeleton && this.isLoading && this.data.length === 0
  }

  get showEmptyState(): boolean {
    return (
      this.loadedOnce &&
      !this.isLoading &&
      !this.isError &&
      this.totalCount === 0
    )
  }

  skeletonWidth(col: TableColumn): string {
    return col.type === 'number' ? '40%' : col.type === 'date' ? '70%' : '85%'
  }

  async reloadGrid(resetPages?: boolean) {
    if (resetPages === true) {
      this.currentPage = 1
      this.firstRecord = 0
    }
    await this.load(this.firstRecord, this.rows)
  }

  async onPage(event: { first: number; rows: number }) {
    await this.load(event.first, event.rows)
  }

  async onPageChange(page: number) {
    this.currentPage = page
    this.firstRecord = (page - 1) * this.rows
    await this.load(this.firstRecord, this.rows)
    this.pageChange.emit({ first: this.firstRecord, rows: this.rows })
  }

  async onRowsChange(newRows: number) {
    if (this.rows === newRows) return
    this.rows = newRows
    this.currentPage = 1
    this.firstRecord = 0
    await this.load(0, this.rows)
    this.pageChange.emit({ first: 0, rows: this.rows })
  }

  async onSort(event: SortEvent) {
    if (this.justResizedColumn) {
      this.justResizedColumn = false
      return
    }
    // The old code loaded offset 0 but left currentPage/firstRecord on the
    // previous page, so the paginator and the data disagreed
    this.currentPage = 1
    this.firstRecord = 0
    await this.load(0, this.rows, event)
  }

  onDropdownFilter(
    event: { filter: string },
    dataSourceKey: string,
    dataKeyField?: string,
    dataDisplayField?: string,
  ): void {
    this.headerDropdownFilterText[dataSourceKey] = event.filter
    this.lookupSearch.emit({
      dataSourceKey,
      q: event.filter,
      dataKeyField,
      dataDisplayField,
      offset: 0,
      limit: this.dropdownLookupPageSize,
    })
  }

  onDropdownShow(dataSourceKey: string, column: TableColumn): void {
    const q = this.headerDropdownFilterText[dataSourceKey]?.trim()
    this.lookupSearch.emit({
      dataSourceKey,
      ...(q ? { q } : {}),
      dataKeyField: column.dataKeyField,
      dataDisplayField: column.dataDisplayField,
      offset: 0,
      limit: this.dropdownLookupPageSize,
    })
  }

  onDropdownLazyLoad(event: DropdownLazyLoadEvent, column: TableColumn): void {
    if (!column.dataSourceKey) return
    const ds = column.dataSourceKey
    const rows = Math.max(event.last - event.first + 1, 1)
    const q = this.headerDropdownFilterText[ds]?.trim()
    this.lookupSearch.emit({
      dataSourceKey: ds,
      ...(q ? { q } : {}),
      dataKeyField: column.dataKeyField,
      dataDisplayField: column.dataDisplayField,
      offset: event.first,
      limit: Math.min(rows, this.dropdownLookupPageSize),
    })
  }

  onNumberFilterChange(event: Event, field: string): void {
    const target = event.target as HTMLInputElement
    const value = target.value
    this.numberFilterValues[field] = value

    const operator = this.numberFilterOperators[field]
    this.onFilterInput(event, field, operator)
  }

  onNumberOperatorChange(operator: string, field: string): void {
    const previous = this.numberFilterOperators[field]
    this.numberFilterOperators[field] = operator

    const isInitialModelSync = previous === undefined
    if (!isInitialModelSync) {
      setTimeout(() => {
        const filterRef = this.textFilters?.find((f) => f.field === field)
        const input = filterRef?.el?.nativeElement?.querySelector('input')
        input?.focus()
      }, 50)
    }

    const existingValue = this.numberFilterValues[field]

    const currentFilters = this.tableFilters()
    const cleaned = Object.fromEntries(
      Object.entries(currentFilters).filter(
        ([k]) => k !== field && !k.startsWith(`${field}:`),
      ),
    ) as TableFilters

    if (existingValue !== undefined) {
      this.filterDelay = 0
      this.tableFilters.set({
        ...cleaned,
        ...(existingValue ? { [`${field}:${operator}`]: existingValue } : {}),
      })
    }
  }

  getGroupDisplayWidth(groupKey: string): number {
    return this.columns().reduce((total, column, colIndex) => {
      if (column.groupKey !== groupKey) {
        return total
      }
      return total + this.getColumnDisplayWidth(column, colIndex)
    }, 0)
  }

  collapseRow(predicate: (item: T) => boolean): void {
    const row = this.data.find(predicate)
    if (!row) return
    const key = get(row as Record<string, unknown>, this.dataKey) as string
    delete this.expandedRows[key]
    this.expandedRows = { ...this.expandedRows }
  }

  expandRow(predicate: (item: T) => boolean): void {
    const row = this.data.find(predicate)
    if (!row) return
    const key = get(row as Record<string, unknown>, this.dataKey) as string
    this.expandedRows = { ...this.expandedRows, [key]: true }
    this.table?.toggleRow(row)
  }

  get expansionRowColspan(): number {
    let n = this.columns()?.length ?? 0
    if (this.expansionTemplate) n += 1
    if (!this.disableSelection) n += 1
    if (this.showActionsColumn()) n += 1
    return n
  }

  get useIntrinsicTableBodyHeight(): boolean {
    if (this.isError) {
      return true
    }
    if (this.isLoading) {
      return false
    }
    return (this.totalCount ?? 0) === 0 && !this.isFiltered
  }

  // totalPages also becomes computed
  get totalPages(): number {
    return Math.ceil((this.totalCount ?? 0) / this.rows)
  }

  get visiblePages(): number[] {
    const total = this.totalPages
    const current = this.currentPage
    const pages: number[] = []

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i)
    } else {
      pages.push(1)
      if (current > 3) pages.push(-1)
      for (
        let i = Math.max(2, current - 1);
        i <= Math.min(total - 1, current + 1);
        i++
      )
        pages.push(i)
      if (current < total - 2) pages.push(-1)
      pages.push(total)
    }
    return pages
  }

  resetGrid() {
    this.resetFilters()
    this.gridReset.emit()
  }

  resetFilters() {
    this.filterDelay = 0
    if (this.table) {
      this.table.sortField = undefined
      this.table.sortOrder = 1
      this.table.multiSortMeta = undefined
      this.table._sortField = undefined
      this.table._sortOrder = 1
      this.table.clear()
      this.table.reset()
    }
    this.selectedItems = []
    this.dropdownFilterValues = {}
    this.numberFilterValues = {}
    this.numberFilterOperators = {}
    this.calendars?.forEach((calendar) => calendar.resetDate())
    this.tableFilters.set({})
    this.textFilters?.forEach((filter) => {
      const inputElement = filter.el?.nativeElement?.querySelector('input')
      if (inputElement) {
        inputElement.value = ''
        inputElement.dispatchEvent(new Event('input', { bubbles: true }))
      }
    })
    this.currentPage = 1
    this.firstRecord = 0
    this.headerDropdownFilterText = {}
  }

  clearSelection() {
    this.selectedItems = []
    this.itemsSelect.emit([])
  }

  expandAll(): void {
    const key = this.dataKey
    this.expandedRows = this.data.reduce(
      (acc, row) => ({
        ...acc,
        [get(row as Record<string, unknown>, key) as string]: true,
      }),
      {} as { [key: string]: boolean },
    )
  }

  isFirstInGroup(column: TableColumn): boolean {
    if (!column.groupKey) return false
    const groupColumns = this.getColumnsByGroup(column.groupKey)
    return groupColumns[0] === column
  }

  isLastInGroup(column: TableColumn): boolean {
    if (!column.groupKey) return false
    const groupColumns = this.getColumnsByGroup(column.groupKey)
    return groupColumns[groupColumns.length - 1] === column
  }

  getGroupLabel(groupKey: string): string {
    const group = this.columnGroups?.find((g) => g.key === groupKey)
    return group?.label || ''
  }

  // Filter methods

  applyFiltersNow(): void {
    this.filterDelay = 0
    this.tableFilters.set({ ...this.tableFilters() })
  }

  onFilterInput(event: Event, field: string, operator: string | undefined) {
    const target = event.target as HTMLInputElement
    if (target) {
      const currentFilters = this.tableFilters()

      // Remove all existing keys for this field before setting the new one
      const cleaned = Object.fromEntries(
        Object.entries(currentFilters).filter(
          ([k]) => k !== field && !k.startsWith(`${field}:`),
        ),
      ) as TableFilters

      const key = `${field}:${operator ?? '='}`

      this.tableFilters.set({
        ...cleaned,
        [key.replace(':=', '')]: target.value || undefined,
      })
    }
  }

  onDateSelect(date: TableFilters, field: string) {
    const currentFilters = this.tableFilters()
    const newFilters = { ...currentFilters, ...date }
    if (date[field] === undefined || date[field] === null) {
      delete newFilters[field]
    }

    this.filterDelay = 0
    this.tableFilters.set(newFilters)
  }

  onDropdownFilterChange(
    selectedValue: object | string | string[] | null,
    field: string,
    dataKeyField?: string,
  ) {
    let filterValue = undefined
    if (
      typeof selectedValue === 'string' ||
      typeof selectedValue === 'boolean'
    ) {
      filterValue = selectedValue
    } else if (Array.isArray(selectedValue)) {
      filterValue = selectedValue.map((item) => {
        if (item == null) return item
        if (typeof item !== 'object') return item
        const path =
          dataKeyField && dataKeyField.length > 0 ? dataKeyField : '_id'
        return get(item, path)
      })
    } else if (typeof selectedValue === 'object' && selectedValue !== null) {
      const path =
        dataKeyField && dataKeyField.length > 0 ? dataKeyField : '_id'
      filterValue = get(selectedValue, path)
    }

    // Update the signal with the new filter value
    const currentFilters = this.tableFilters()
    const newFilters = { ...currentFilters, [field]: filterValue }

    this.filterDelay = 0
    this.tableFilters.set(newFilters)
  }

  getOrderCoverage(coverage: string, displayCode?: boolean) {
    const coverageModel = (
      this.dataLookups?.['coverage'] as EntityStatusDesc<Coverage>[] | undefined
    )?.find((cov) => cov.code === coverage)

    return displayCode
      ? coverageModel?.code?.toLowerCase()
      : coverageModel?.name
  }

  getPickingCoverage(
    orderId: string,
    displayCode?: boolean,
    lowercase = false,
  ) {
    const status = (
      this.dataLookups?.['coverage'] as PickingListOrder[] | undefined
    )?.find((cov) => cov._id === orderId)?.status

    return COVERAGE_TYPES.map((item) => ({
      ...item,
      name: this.translationService.translate(item.name),
    }))
      .filter((_status) => _status.code === status)
      .map((_status: EntityStatusDesc<Coverage>) =>
        lowercase
          ? (displayCode === true ? _status.code : _status.name)?.toLowerCase()
          : displayCode === true
            ? _status.code
            : _status.name,
      )
  }

  getExecutionStatus(
    activity: Activity,
  ): EntityStatusDesc<ExecutionStatus> | undefined {
    const executionStatus = (
      this.dataLookups?.['executions'] as Execution[] | undefined
    )?.find((ex) => ex._id === activity.lastExecutionId)?.status
    return EXECUTION_STATUSES.find((s) => s.code === executionStatus)
  }

  getUnusable(orderId: string): PickingUnusableProduct | undefined {
    return (
      this.dataLookups?.['unusable'] as PickingUnusableProduct[] | undefined
    )?.find((o) => o.orderId === orderId)
  }

  catalogLocale(value: CatalogLocales<string>[]) {
    return this.catalogLocalePipe.transform(
      value,
      this.scope,
      this.scope === undefined,
    )
  }

  familyCompletenessPercent(value: unknown): number | null {
    if (value == null || value === '') {
      return null
    }
    const raw = this.catalogLocalePipe.transform(
      value as CatalogLocales<string>[],
      this.scope,
      true,
      false,
      true,
    )
    if (raw === '' || raw === null || raw === undefined) {
      return null
    }
    const n = typeof raw === 'number' ? raw : parseFloat(String(raw))
    return Number.isFinite(n) ? n : null
  }

  // Parsing methods

  parseBarcodes(barcodes?: ProductBarcode[], newLines?: boolean): string {
    if (barcodes?.length) {
      return barcodes
        .map((b) => b.value)
        .join(', ')
        .replaceAll(newLines ? ', ' : '', newLines ? '\n' : '')
    }
    return ''
  }

  parseCatalogLocale(locales?: CatalogLocales<string>[]): string {
    const catalogCode = this.scope?.catalogCode ?? 'default'
    const localeCode = this.scope?.locale ?? 'default'
    const defaultLocale: CatalogLocales<string> | undefined = locales?.find(
      (l) => l.catalogCode === 'default',
    )
    const locale: CatalogLocales<string> | undefined = locales?.find(
      (l) => l.catalogCode === catalogCode,
    )

    return (
      locale?.locales[localeCode] ?? defaultLocale?.locales[localeCode] ?? ''
    )
  }

  parseGoodsReceiveByNum(value?: number): string | undefined {
    if (!value) {
      return undefined
    }
    const goodsReceives: GoodsReceive[] | undefined = this.dataLookups?.[
      'goodsReceives'
    ] as GoodsReceive[] | undefined
    return goodsReceives?.find((g) => g.header.orderNumber === value)?.header
      .rifDoc
  }

  parseDisplayField(item: unknown, displayField?: string): string {
    const fields = displayField?.split('|')
    if (
      fields &&
      fields.length > 1 &&
      typeof item === 'object' &&
      item !== null
    ) {
      const obj = item as Record<string, unknown>
      const value1 = obj[fields[0]]
      const value2 = obj[fields[1]]
      return `${value1 ?? ''} ${value2 ?? ''}`
    }
    return ''
  }

  parseStatusHistoryTime(statusHistory?: StatusHistory[]) {
    if (!statusHistory || statusHistory.length < 2) {
      return ''
    }
    return `${((new Date(statusHistory[statusHistory.length - 1].date).getTime() - new Date(statusHistory[0].date).getTime()) / 60000).toFixed(1)} min`
  }

  parseLocation(locationId?: string): string {
    const locations: Location[] | undefined = this.dataLookups?.[
      'locations'
    ] as Location[] | undefined
    const location: Location | undefined = locations?.find(
      (l) => l._id === locationId,
    )
    const dock: Location | undefined = location?.dockId
      ? locations?.find((l) => l._id === location?.dockId)
      : undefined
    return `${location?.path ?? ''}${dock ? ' [' + (dock?.path ?? '') + ']' : ''}`
  }

  parseProductCategories(
    prodCategories: ProductCatalogCategory[] | undefined,
  ): string {
    if (!prodCategories || prodCategories.length < 1) {
      return ''
    }
    const categories: Category[] | undefined = this.dataLookups?.[
      'categories'
    ] as Category[] | undefined
    if (!categories || categories.length < 1) {
      return ''
    }
    const ids: string[] = []
    prodCategories
      .filter((c) => c.catalogCode === (this.scope?.catalogCode ?? 'default'))
      .map((c) => ids.push(...c.ids))
    return categories
      .filter((c) => ids.includes(c._id))
      .map((c) => c.name)
      .map((v) => v[this.scope?.locale ?? 'default'] ?? '')
      .join(', ')
  }

  parseProductName(productId?: string) {
    const products: Product[] | undefined = this.dataLookups?.['products'] as
      | Product[]
      | undefined
    const product: Product | undefined = products?.find(
      (p) => p._id === productId,
    )
    const name =
      getCatalogValue(
        product?.name,
        this.scope?.catalogCode,
        this.scope?.locale,
      ) ?? ''
    return name.length > 30 ? `${name.substring(0, 27)}...` : name
  }

  parseProductParent(productId?: string) {
    const products: Product[] | undefined = this.dataLookups?.['parents'] as
      | Product[]
      | undefined
    const product: Product | undefined = products?.find(
      (p) => p._id === productId,
    )
    if (!product) {
      return ''
    }
    const name =
      getCatalogValue(
        product?.name,
        this.scope?.catalogCode,
        this.scope?.locale,
      ) ?? ''
    return `${product.SKU}${name.length ? ` - ${name}` : ''}`
  }

  parseManufacturer(manufacturerId?: string) {
    const manufacturers: Manufacturer[] | undefined = this.dataLookups?.[
      'manufacturers'
    ] as Manufacturer[] | undefined
    const manufacturer: Manufacturer | undefined = manufacturers?.find(
      (m) => m._id === manufacturerId,
    )
    return manufacturer?.name ?? ''
  }

  parseMultiSelectAttribute(
    value: string | undefined,
    dataSourceKey: string,
  ): string {
    if (value === undefined || value.length < 1) {
      return ''
    }
    const options: AttributeOption[] | undefined = this.dataLookups?.[
      dataSourceKey
    ] as AttributeOption[] | undefined
    if (!options?.length) {
      return value
    }
    const codes = value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    const labels = codes.map((code) => {
      const attr = options.find((a) => a.value === code)
      return attr?.label?.default ?? code
    })
    return labels.join(', ')
  }

  parseUserId(userId?: string): string | undefined {
    const users: User[] | undefined = this.dataLookups?.['users'] as
      | User[]
      | undefined
    const user: User | undefined = users?.find((u) => u._id === userId)
    return user ? parseUser(user) : undefined
  }

  parsePriority(value: number): string {
    return value < 40 ? 'Bassa' : value < 70 ? 'Media' : 'Alta'
  }

  parseCurrency(currency: Currency): string {
    return `${currency.name} [${currency.code}]`
  }

  parseEntityName(): string {
    return this.entityName.replaceAll('_', ' ')
  }

  parseSalesChannel(product: Product): string {
    const channels: Channel[] | undefined = this.dataLookups?.['channels'] as
      | Channel[]
      | undefined
    if (!channels || channels.length < 1) {
      return ''
    }
    const ids: string[] = []
    product.externalSKUs?.map((e) =>
      e.channelId ? ids.push(e.channelId) : undefined,
    )
    if (ids.length < 1) {
      return ''
    }
    return channels
      .filter((c) => ids.includes(c._id))
      .map((c) => c.name)
      .join(', ')
  }

  parseRoles(userRoles: UserRole[]): string {
    const roles: Role[] | undefined = this.dataLookups?.['roles'] as
      | Role[]
      | undefined

    if (!roles?.length || !userRoles?.length) {
      return ''
    }

    const roleIds = userRoles.map((ur) => ur._id)

    return roles
      .filter((r) => roleIds.includes(r._id))
      .map((r) => r.label)
      .join(', ')
  }

  parseSites(sites?: ProductSite[]): string | undefined {
    if (!sites || !sites.length) {
      return undefined
    }
    const sitesIds = sites.map((s) => s.locationId)
    const locations = this.dataLookups?.['locations'] as Location[] | undefined
    const sitesLocations =
      locations?.filter((l) => sitesIds.includes(l._id)) ?? []
    if (!sitesLocations.length) {
      return undefined
    }
    return sitesLocations.map((l) => l.path).join(', ')
  }

  parseStatusHistoryUser(
    statusHistory: StatusHistory[],
    status: string,
  ): string {
    const userId = statusHistory.find((sh) => sh.status === status)?.userId
    const users = this.dataLookups?.['users'] as User[] | undefined
    const user = users?.find((u) => u._id === userId)
    return user ? parseUser(user as User) : ''
  }

  parseSuppliersList(prodSuppliers: ProductSupplier[] | undefined): string {
    const supplierIds = prodSuppliers?.map((s) => s.supplierId)
    if (!supplierIds || supplierIds.length < 1) {
      return ''
    }
    const suppliers = this.dataLookups?.['suppliers'] as Supplier[] | undefined
    if (!suppliers || suppliers.length < 1) {
      return ''
    }
    const filtered = suppliers.filter((s) => supplierIds.includes(s._id))
    return filtered.map((s) => s.businessName).join(', ')
  }

  parseTrackingNumber(shipments?: OrderShipment[]): string {
    if (!shipments || !shipments.length) {
      return ''
    }

    const lastShipment = [...shipments]
      .reverse()
      .find((s) => s.status === 'REQUESTED' || s.status === 'SHIPPED')

    return lastShipment?.trackingNumbers?.join(', ') ?? ''
  }

  updateQty(
    item: T,
    event: {
      _id: string
      qty: number
    },
  ) {
    if (this.selectOnQtyEdit) {
      const key = this.dataKey
      const itemRecord = item as Record<string, unknown>
      const id = itemRecord[key]
      const alreadySelected = this.selectedItems.some(
        (s) => (s as Record<string, unknown>)[key] === id,
      )
      if (!alreadySelected) {
        this.selectedItems = [...this.selectedItems, item]
        this.itemsSelect.emit(this.selectedItems)
      }
    }
    this.qtyChange.emit(event)
  }

  isProductQuantifiable(product: Product): boolean {
    return (
      PRODUCT_TYPES.find((t) => t.code === product.productType)
        ?.isQuantifiable ?? false
    )
  }

  getScopeQtyString(product: Product, qtyType: ProuctQtyType): string {
    return this.getScopeQtyNumber(product, qtyType).toString()
  }

  getScopeQtyNumber(product: Product, qtyType: ProuctQtyType): number {
    const productWarehouse = this.scope?.warehouseId
      ? product.warehouses.find((w) => w._id === this.scope?.warehouseId)
      : undefined
    switch (qtyType) {
      case 'availableQty':
        return productWarehouse?.availableQty ?? product.availableQty
      case 'incomingQty':
        return productWarehouse?.incomingQty ?? product.incomingQty
      case 'onHandQty':
        return productWarehouse?.onHandQty ?? product.onHandQty
      case 'orderedQty':
        return product.receivingQty ?? 0
      case 'scrappedQty':
        return productWarehouse?.scrappedQty ?? product.scrappedQty ?? 0
      default:
        return 0
    }
  }

  // Export methods

  async exportGrid(exportAll = false, custFileName?: string): Promise<void> {
    let dataToExport: T[] = []

    if (exportAll === true) {
      dataToExport = await this.getData(true)
    } else if (this.selectedItems.length > 0) {
      dataToExport = this.selectedItems
    }

    await this.exportDataToExcel(dataToExport, custFileName)
  }

  private async exportDataToExcel(
    dataToExport: T[],
    custFileName?: string,
  ): Promise<void> {
    const workbook = new Workbook()
    const worksheet = workbook.addWorksheet(
      custFileName || this.fileName || 'data',
    )

    const exportableColumns = this.columns().filter(
      (col) => !col.excludeFromExport,
    )

    const headerRow = worksheet.addRow(
      exportableColumns.map((col) => col.label),
    )
    headerRow.font = { bold: true }

    // Freeze header row and enable Excel's AutoFilter (includes sorting in UI)
    worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 1 }]
    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: exportableColumns.length },
    }

    dataToExport.forEach((item) => {
      // Generate row
      const row = worksheet.addRow(
        exportableColumns.map((col) => {
          const rawValue = get(item, col.field || '')
          return rawValue
        }),
      )

      // Apply customizeExportCell callback and coerce cell value/format based on TableColumn.type
      // Use manual iteration instead of eachCell to ensure all columns are processed
      for (let colIndex = 1; colIndex <= exportableColumns.length; colIndex++) {
        const cell = row.getCell(colIndex)
        const column = exportableColumns[colIndex - 1]
        const rawValue = get(item, column.field || '')
        const excelCell = cell as Cell

        // Apply customizeExportCell callback if provided
        const customizedValue = this.customizeExportCell
          ? this.customizeExportCell({
              data: item,
              column: column,
              value: rawValue,
              excelCell: excelCell,
              dataLookups: this.dataLookups,
            })
          : rawValue

        // Set the customized value
        excelCell.value = customizedValue
        excelCell.numFmt = this.getNumFmt(column.field)

        // Coerce cell value and Excel format based on TableColumn.type
        const columnType = column?.type
        switch (columnType) {
          case 'date': {
            // Ensure value is a Date for Excel and apply number format
            let dateValue: Date | null = null

            if (customizedValue instanceof Date) {
              dateValue = customizedValue
            } else if (customizedValue) {
              // Parse ISO string (UTC) and convert to local time
              // ExcelJS stores dates as UTC internally, so we need to adjust
              // to retain local time when Excel opens it
              const localDate = new Date(customizedValue)
              // Adjust by subtracting timezone offset to ensure Excel displays correct local time
              dateValue = new Date(
                localDate.getTime() - localDate.getTimezoneOffset() * 60000,
              )
            }

            excelCell.value = dateValue
            if (dateValue) {
              excelCell.numFmt = column.dateFormat || 'dd/mm/yyyy HH:mm'
            }
            break
          }
          case 'number': {
            if (
              customizedValue === null ||
              customizedValue === undefined ||
              customizedValue === ''
            ) {
              excelCell.value = null
              break
            }
            const num =
              typeof customizedValue === 'number'
                ? customizedValue
                : Number(customizedValue)
            excelCell.value = Number.isFinite(num) ? (num as number) : null
            excelCell.alignment = { horizontal: 'right' }
            break
          }
          case 'boolean': {
            excelCell.value = this.customizeExportCell
              ? customizedValue
              : Boolean(customizedValue)
            break
          }
          default:
            break
        }
      }
    })

    worksheet.columns.forEach((column, i) => {
      const columnWidth =
        exportableColumns[i].width ?? exportableColumns[i].width
      column.width = columnWidth ? columnWidth / 3 : 15
    })

    const buffer = await workbook.xlsx.writeBuffer()
    saveAs(
      new Blob([buffer], { type: 'application/octet-stream' }),
      `${(custFileName ?? this.fileName).replaceAll(' ', '_') || 'export'}_${getFileDate()}.xlsx`,
    )
  }

  private getNumFmt(field?: string) {
    switch (field) {
      case 'returnedRowsCount':
      case 'rowsCount':
      case 'referenceOrder.number':
      case 'recoveryOrder.number':
      case 'productsScrappedCount':
      case 'productsReturnedCount':
      case 'productsOrderedCount':
      case 'header.returnNumber':
      case 'productsCount':
        return '0'
      case 'header.totalReturnAmount':
      case 'header.refundAmount':
      case 'totalWeight':
      case 'estimatedWeight':
      case 'grossWeight':
      case 'netWeight':
        return '0.00'
      case 'estimatedVolume':
      case 'totalVolume':
      case 'volume.total':
        return '0.0000'
      default:
        return '@'
    }
  }

  getFGColor(color: string) {
    return color ? (isLightColor(color) ? '#000' : '#fff') : '#000'
  }

  imageUrl(path: string, tenantId: string): string {
    return this.fsPipe.transform(path, tenantId)
  }

  numberParser(event: KeyboardEvent, disableDecimals?: boolean) {
    if (event.metaKey || event.ctrlKey) {
      return
    }

    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.stopPropagation()
      return
    }

    const controlKeys = [
      'Backspace',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Delete',
      'Enter',
    ]
    if (controlKeys.includes(event.key)) {
      return
    }

    if (/[0-9]/.test(event.key)) {
      return
    }

    const isComma = event.key === ','
    const isDotOrNumpad =
      event.key === '.' ||
      event.key === 'Decimal' ||
      event.code === 'NumpadDecimal'

    if (isComma || isDotOrNumpad) {
      if (disableDecimals) {
        event.preventDefault()
        return
      }

      const input = event.target as HTMLInputElement
      if (input.value.includes(',') || input.value.includes('.')) {
        event.preventDefault()
        return
      }

      // No comma yet — allow the native key as-is
      // PrimeNG's own input handler will normalize dot → comma for Italian locale
      return
    }

    event.preventDefault()
  }

  // Template management
  @ViewChild('abcClassBadge') abcClassBadge!: TemplateRef<unknown>
  @ViewChild('aisle') aisle!: TemplateRef<unknown>
  @ViewChild('area') area!: TemplateRef<unknown>
  @ViewChild('attributeMultiSelect') attributeMultiSelect!: TemplateRef<unknown>
  @ViewChild('attachments') attachments!: TemplateRef<unknown>
  @ViewChild('availableQty') availableQty!: TemplateRef<unknown>
  @ViewChild('barcodes') barcodes!: TemplateRef<unknown>
  @ViewChild('bulkUpdateError') bulkUpdateError!: TemplateRef<unknown>
  @ViewChild('bulkUpdateResults') bulkUpdateResults!: TemplateRef<unknown>
  @ViewChild('carrier') carrier!: TemplateRef<unknown>
  @ViewChild('catalogLocales') catalogLocales!: TemplateRef<unknown>
  @ViewChild('clipboard') clipboard!: TemplateRef<unknown>
  @ViewChild('compartment') compartment!: TemplateRef<unknown>
  @ViewChild('completion') completion!: TemplateRef<unknown>
  @ViewChild('coverageOrder') coverageOrder!: TemplateRef<unknown>
  @ViewChild('coverageOrderPicking') coverageOrderPicking!: TemplateRef<unknown>
  @ViewChild('currency') currency!: TemplateRef<unknown>
  @ViewChild('defaultValue') defaultValue!: TemplateRef<unknown>
  @ViewChild('eecCountry') eecCountry!: TemplateRef<unknown>
  @ViewChild('executionStatus') executionStatus!: TemplateRef<unknown>
  @ViewChild('familyCompleteness') familyCompleteness!: TemplateRef<unknown>
  @ViewChild('goodsReceive') goodsReceive!: TemplateRef<unknown>
  @ViewChild('goodsReceiveByOrdNum') goodsReceiveByOrdNum!: TemplateRef<unknown>
  @ViewChild('goodsReceiveStatus') goodsReceiveStatus!: TemplateRef<unknown>
  @ViewChild('image') image!: TemplateRef<unknown>
  @ViewChild('importantNote') importantNote!: TemplateRef<unknown>
  @ViewChild('incomingQty') incomingQty!: TemplateRef<unknown>
  @ViewChild('manufacturerName') manufacturerName!: TemplateRef<unknown>
  @ViewChild('movedQty') movedQty!: TemplateRef<unknown>
  @ViewChild('notes') notes!: TemplateRef<unknown>
  @ViewChild('onHandQty') onHandQty!: TemplateRef<unknown>
  @ViewChild('orderPackages') orderPackages!: TemplateRef<unknown>
  @ViewChild('packedOrders') packedOrders!: TemplateRef<unknown>
  @ViewChild('packagesVolume') packagesVolume!: TemplateRef<unknown>
  @ViewChild('packagesWeight') packagesWeight!: TemplateRef<unknown>
  @ViewChild('packingActions') packingActions!: TemplateRef<unknown>
  @ViewChild('path') path!: TemplateRef<unknown>
  @ViewChild('pickingListRef') pickingListRef!: TemplateRef<unknown>
  @ViewChild('pickProduct') pickProduct!: TemplateRef<unknown>
  @ViewChild('position') position!: TemplateRef<unknown>
  @ViewChild('price') price!: TemplateRef<unknown>
  @ViewChild('priceWithCatalog') priceWithCatalog!: TemplateRef<unknown>
  @ViewChild('priceWithFour') priceWithFour!: TemplateRef<unknown>
  @ViewChild('priority') priority!: TemplateRef<unknown>
  @ViewChild('productCategories') productCategories!: TemplateRef<unknown>
  @ViewChild('productLink') productLink!: TemplateRef<unknown>
  @ViewChild('productName') productName!: TemplateRef<unknown>
  @ViewChild('productParent') productParent!: TemplateRef<unknown>
  @ViewChild('productPhoto') productPhoto!: TemplateRef<unknown>
  @ViewChild('productSites') productSites!: TemplateRef<unknown>
  @ViewChild('productQties') productQties!: TemplateRef<unknown>
  @ViewChild('qty') qty!: TemplateRef<unknown>
  @ViewChild('receivingQty') receivingQty!: TemplateRef<unknown>
  @ViewChild('returnedRowsCount') returnedRowsCount!: TemplateRef<unknown>
  @ViewChild('role') role!: TemplateRef<unknown>
  @ViewChild('rowsCount') rowsCount!: TemplateRef<unknown>
  @ViewChild('rowsOrderedCount') rowsOrderedCount!: TemplateRef<unknown>
  @ViewChild('rowsReturnedCount') rowsReturnedCount!: TemplateRef<unknown>
  @ViewChild('rowsScrappedCount') rowsScrappedCount!: TemplateRef<unknown>
  @ViewChild('sku') sku!: TemplateRef<unknown>
  @ViewChild('sales') sales!: TemplateRef<unknown>
  @ViewChild('salesChannel') salesChannel!: TemplateRef<unknown>
  @ViewChild('selectRow') selectRow!: TemplateRef<unknown>
  @ViewChild('scrappedQty') scrappedQty!: TemplateRef<unknown>
  @ViewChild('shelf') shelf!: TemplateRef<unknown>
  @ViewChild('status') status!: TemplateRef<unknown>
  @ViewChild('statusHistoryTime') statusHistoryTime!: TemplateRef<unknown>
  @ViewChild('stHiPackedBy') stHiPackedBy!: TemplateRef<unknown>
  @ViewChild('stHiProcessedBy') stHiProcessedBy!: TemplateRef<unknown>
  @ViewChild('suppliers') suppliers!: TemplateRef<unknown>
  @ViewChild('suppliersSku') suppliersSku!: TemplateRef<unknown>
  @ViewChild('tagModel') tagModel!: TemplateRef<unknown>
  @ViewChild('tags') tags!: TemplateRef<unknown>
  @ViewChild('tooltip') tooltip!: TemplateRef<unknown>
  @ViewChild('trackingNumber') trackingNumber!: TemplateRef<unknown>
  @ViewChild('unusableOrder') unusableOrder!: TemplateRef<unknown>
  @ViewChild('user') user!: TemplateRef<unknown>

  setTemplateMap() {
    this.templateMap = {
      abcClassBadge: this.abcClassBadge,
      aisle: this.aisle,
      area: this.area,
      attributeMultiSelect: this.attributeMultiSelect,
      attachments: this.attachments,
      availableQty: this.availableQty,
      barcodes: this.barcodes,
      bulkUpdateError: this.bulkUpdateError,
      bulkUpdateResults: this.bulkUpdateResults,
      carrier: this.carrier,
      catalogLocales: this.catalogLocales,
      clipboard: this.clipboard,
      compartment: this.compartment,
      completion: this.completion,
      coverageOrder: this.coverageOrder,
      coverageOrderPicking: this.coverageOrderPicking,
      currency: this.currency,
      defaultValue: this.defaultValue,
      eecCountry: this.eecCountry,
      executionStatus: this.executionStatus,
      familyCompleteness: this.familyCompleteness,
      goodsReceive: this.goodsReceive,
      goodsReceiveByOrdNum: this.goodsReceiveByOrdNum,
      goodsReceiveStatus: this.goodsReceiveStatus,
      image: this.image,
      importantNote: this.importantNote,
      incomingQty: this.incomingQty,
      manufacturerName: this.manufacturerName,
      movedQty: this.movedQty,
      notes: this.notes,
      onHandQty: this.onHandQty,
      orderPackages: this.orderPackages,
      packedOrders: this.packedOrders,
      packagesVolume: this.packagesVolume,
      packagesWeight: this.packagesWeight,
      packingActions: this.packingActions,
      path: this.path,
      pickingListRef: this.pickingListRef,
      pickProduct: this.pickProduct,
      position: this.position,
      price: this.price,
      priceWithCatalog: this.priceWithCatalog,
      priceWithFour: this.priceWithFour,
      priority: this.priority,
      productCategories: this.productCategories,
      productLink: this.productLink,
      productName: this.productName,
      productParent: this.productParent,
      productPhoto: this.productPhoto,
      productSites: this.productSites,
      productQties: this.productQties,
      qty: this.qty,
      receivingQty: this.receivingQty,
      returnedRowsCount: this.returnedRowsCount,
      role: this.role,
      rowsCount: this.rowsCount,
      rowsOrderedCount: this.rowsOrderedCount,
      rowsReturnedCount: this.rowsReturnedCount,
      rowsScrappedCount: this.rowsScrappedCount,
      sku: this.sku,
      sales: this.sales,
      salesChannel: this.salesChannel,
      selectRow: this.selectRow,
      scrappedQty: this.scrappedQty,
      shelf: this.shelf,
      status: this.status,
      statusHistoryTime: this.statusHistoryTime,
      stHiPackedBy: this.stHiPackedBy,
      stHiProcessedBy: this.stHiProcessedBy,
      suppliers: this.suppliers,
      suppliersSku: this.suppliersSku,
      tagModel: this.tagModel,
      tags: this.tags,
      tooltip: this.tooltip,
      trackingNumber: this.trackingNumber,
      unusableOrder: this.unusableOrder,
      user: this.user,
    }
  }
}
